import { useState } from 'react';
import { useI18n } from '@/lib/i18n';
import { Button, Dialog } from '@/ui';

/**
 * "Watch a demo" (Stage 4, wave B) — the film, not a live workspace. It
 * plays in a dialog with `preload="none"` and a poster, so a visitor who
 * never presses play never fetches the 37MB file (see web/public/demo-poster.jpg
 * and Caddy's /media/ mount — the film itself is deliberately not in the repo).
 * Identical everywhere the button appears (Landing's hero, its closing panel,
 * Contact) so the three can never drift out of sync.
 *
 * This used to also reveal a quiet "Have an access code?" toggle that
 * redeemed a code for a live demo workspace (`POST /api/public/demo`). The
 * owner ruled out demo access to the product entirely (24 Sep and 6 Oct
 * 2026: "there will be no Demo access") — and once the operator-side console
 * for issuing codes was removed, no new code could ever be redeemed, so that
 * toggle was a dead end for every prospect who tried it. It is gone, along
 * with the route it called. The film is unrelated: nobody presses play
 * expecting a workspace, nothing is created, there is no session — so it
 * stays.
 */
export function DemoAccess({ label, size = 'lg' }: { label: string; size?: 'md' | 'lg' }) {
  const { t } = useI18n();
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <div className="demo-access">
      <Button variant="secondary" size={size} onClick={() => setVideoOpen(true)}>
        {label}
      </Button>

      <Dialog open={videoOpen} onClose={() => setVideoOpen(false)} title={label} closeLabel={t.common.close} size="lg">
        {/* preload="none": nothing is fetched until the visitor presses play,
            even though the dialog (and this element) now exist in the DOM. */}
        <video controls preload="none" poster="/demo-poster.jpg" className="demo-access-video">
          <source src="/media/product-demo.mp4" type="video/mp4" />
        </video>
      </Dialog>
    </div>
  );
}

export default DemoAccess;
